// P&P Rounds table.
//
// 2026-09-07: rows come from pp_tracking (a round = the orders moved to
// Printed together, until the last is processed; every step recorded) — see
// docs/PP-TRACKING.md. Days before tracking, or without a tracked round, still
// show the old printed_at clustering, marked "estimated". Click a tracked row
// for the step timeline.
import React, { useEffect, useMemo, useState } from 'react';
import { Session, fmtTime, fmtDur, speedColor, speedRowBg } from './shared';
import RoundDetail from './RoundDetail';

/** An OPEN round's live figures (Fernando, 2026-09-22): the clock keeps running on the
 *  screen from the server's snapshot, so the speed "if the round ended now" moves without a
 *  reload. Elapsed = server elapsed + time since the data arrived; speed = counted orders /
 *  elapsed; the pace line = processed so far / elapsed. */
function liveNow(s: Session, receivedAt: number, now: number) {
  if (s.live_elapsed_minutes == null) return null;
  const elapsed = s.live_elapsed_minutes + (now - receivedAt) / 60000;
  const ifEnded = elapsed >= 3 && s.orders ? Math.round((s.orders / (elapsed / 60)) * 10) / 10 : null;
  const done = s.archived_orders ?? 0;
  const pace = elapsed >= 3 && done ? Math.round((done / (elapsed / 60)) * 10) / 10 : null;
  return { elapsed, ifEnded, pace, done };
}

export default function PpRoundsTable({ rounds, isManager }: { rounds: Session[]; isManager: boolean }) {
  const [open, setOpen] = useState<number | null>(null);
  // when THIS set of rows arrived — the live clock counts on from here
  const receivedAt = useMemo(() => Date.now(), [rounds]);
  const hasOpen = rounds.some(s => s.source === 'tracked' && s.status === 'open' && s.live_elapsed_minutes != null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!hasOpen) return;
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, [hasOpen]);
  const cols = isManager ? 14 : 13;
  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 mb-5">
      {open != null && <RoundDetail roundId={open} onClose={() => setOpen(null)} />}
      <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-white">P&P Rounds</h2>
          <p className="text-xs text-gray-600 mt-0.5">
            A round starts when orders are moved to Printed and ends when the last one is processed;
            speed = orders per hour over that whole cycle; orders diverted to Label issue / Hard to find / Can't find still count, orders moved back to New do not.
            Picking = View picking list → Complete; packing = first note scan → last order packed. Click a row for every step.
            Rows marked <span className="text-gray-500">est.</span> are from before step tracking.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> ≥30/hr</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" /> 20–29</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> &lt;20</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-gray-800">
              <th className="px-3 py-2 text-left font-medium">Date</th>
              {isManager && <th className="px-3 py-2 text-left font-medium">Started by</th>}
              <th className="px-3 py-2 text-left font-medium">Picker</th>
              <th className="px-3 py-2 text-left font-medium">Packer</th>
              <th className="px-3 py-2 text-left font-medium">Start</th>
              <th className="px-3 py-2 text-left font-medium">End</th>
              <th className="px-3 py-2 text-right font-medium">Picking</th>
              <th className="px-3 py-2 text-right font-medium">Packing</th>
              <th className="px-3 py-2 text-right font-medium">Total</th>
              <th className="px-3 py-2 text-right font-medium" title="Metres walked on the floor plan (P&P station → the recorded stops → back)">Walked</th>
              <th className="px-3 py-2 text-right font-medium">Orders</th>
              <th className="px-3 py-2 text-right font-medium" title="Pieces packed by scanning the barcode · pieces packed with the Pack button (no barcode)">Scans</th>
              <th className="px-3 py-2 text-right font-medium" title="Pieces per hour: picking (pieces ÷ picking time) · packing (pieces ÷ packing time)">Pieces/h</th>
              <th className="px-3 py-2 text-right font-medium">Solving</th>
              <th className="px-3 py-2 text-right font-medium" title="Orders per hour over the whole round. The target is 20/h: below it red, 20–30 yellow, 30 and over green — a fixed line, not anyone's pace">Speed <span className="font-normal text-gray-600">≥20</span></th>
            </tr>
          </thead>
          <tbody>
            {rounds.length === 0 ? (
              <tr>
                <td colSpan={cols} className="px-4 py-10 text-center text-gray-600 text-sm">
                  No rounds in this period
                </td>
              </tr>
            ) : rounds.map((s, i) => {
              const tracked = s.source === 'tracked';
              const live = tracked && s.status === 'open' ? liveNow(s, receivedAt, now) : null;
              return (
              <tr key={s.round_id ?? `est-${i}`}
                  onClick={() => tracked && s.round_id && setOpen(s.round_id)}
                  className={`border-b border-gray-800/50 transition-colors hover:bg-gray-800/20 ${speedRowBg(live ? live.ifEnded : s.orders_per_hour)} ${tracked ? 'cursor-pointer' : ''}`}>
                <td className="px-3 py-2.5 text-gray-300 whitespace-nowrap">
                  {s.date}
                  {!tracked && <span className="ml-1.5 text-[10px] text-gray-600 uppercase" title="Estimated from delivery-note print times (before step tracking)">est.</span>}
                  {tracked && s.status === 'open' && <span className="ml-1.5 text-[10px] text-blue-400 uppercase">open</span>}
                  {tracked && s.auto_closed && <span className="ml-1.5 text-[10px] text-gray-500 uppercase" title="No activity for 12 h — closed at its last real activity">auto-closed</span>}
                </td>
                {isManager && <td className="px-3 py-2.5 text-gray-400">{tracked ? (s.starter || '—') : s.display_name}</td>}
                <td className="px-3 py-2.5 text-gray-200">
                  {tracked ? (s.picker || <span className="text-gray-700">—</span>) : <span className="text-gray-700">—</span>}
                  {tracked && s.picker_device && <span className="text-gray-600 text-xs"> · {s.picker_device}</span>}
                </td>
                <td className="px-3 py-2.5 text-gray-200">
                  {tracked ? (s.packers_detail?.length
                    ? s.packers_detail.map(p => (
                        <div key={p.packer} className="whitespace-nowrap">{p.packer}
                          <span className="text-gray-500 text-xs"> {p.scans}{p.manual ? <span className="text-amber-400" title="packed with the Pack button, no barcode"> · {p.manual} manual</span> : null}</span>
                        </div>))
                    : (s.packers || <span className="text-gray-700">—</span>)) : <span className="text-gray-700">—</span>}
                </td>
                <td className="px-3 py-2.5 text-gray-400">{fmtTime(s.round_start)}</td>
                <td className="px-3 py-2.5 text-gray-400">
                  {s.round_end
                    ? fmtTime(s.round_end)
                    : <span className="text-yellow-600 text-xs" title="A round ends when all its orders are processed">
                        {s.archived_orders ?? 0} of {s.orders} processed
                      </span>}
                </td>
                <td className="px-3 py-2.5 text-right text-gray-400 whitespace-nowrap">
                  {tracked ? <>{fmtDur(s.picking_minutes ?? null)}{s.picking_pct != null && <span className="text-gray-600 text-xs"> {s.picking_pct}%</span>}
                    {s.walk_minutes != null && <div className="text-[11px] text-gray-500" title={`Split by ${s.pace_source === 'own' ? "this person's own pace" : 'the warehouse average pace'}${s.walking_speed ? ` (${s.walking_speed} m/s)` : ''}: walking at that speed over the metres, the time at the shelves (per stop + per piece), box changes, Back re-checks; the rest is pauses and searching`}>
                      walk {fmtDur(s.walk_minutes)} · shelves {fmtDur(s.shelf_minutes ?? null)}{s.other_minutes ? ` · other ${fmtDur(s.other_minutes)}` : ''}{s.pace_source === 'warehouse' ? ' ·  wh avg' : ''}</div>}
                    {s.tapped_through && <div className="text-[11px] text-amber-400" title="More than a quarter of the steps took under 3 s: tapped through (two people picking?) — left out of the pace fit; the round's speed still counts">tapped through</div>}
                    {s.swaps ? <div className="text-[11px] text-gray-500" title="Box swaps: the walk to the P&P station, the box change and Continue — timed from the swap screen to Continue on the tablet">
                      {s.swaps} swap{s.swaps === 1 ? '' : 's'}{s.swap_minutes != null ? ` · ${fmtDur(s.swap_minutes)}` : ''}{s.swaps_timed != null && s.swaps_timed < s.swaps ? ` (${s.swaps_timed} timed)` : ''}</div> : null}</> : <span className="text-gray-700">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right text-gray-400 whitespace-nowrap">
                  {tracked ? <>{fmtDur(s.packing_minutes ?? null)}{s.packing_pct != null && <span className="text-gray-600 text-xs"> {s.packing_pct}%</span>}</> : <span className="text-gray-700">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right text-gray-400 whitespace-nowrap">
                  {live
                    ? <span className="text-blue-300" title="Running — the round is still open">{fmtDur(live.elapsed)} <span className="text-xs text-blue-400/70">so far</span></span>
                    : fmtDur(s.duration_minutes)}
                </td>
                <td className="px-3 py-2.5 text-right text-gray-400 whitespace-nowrap">
                  {tracked && s.metres != null
                    ? <>{Math.round(s.metres)} m{s.metres_per_order != null && <span className="text-gray-600 text-xs"> {s.metres_per_order}/order</span>}
                      {s.metres_swaps ? <div className="text-[11px] text-gray-500" title="Of the metres: the box swaps' walks to the P&P station and back">+{Math.round(s.metres_swaps)} m for the swaps</div> : null}</>
                    : <span className="text-gray-700">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right font-semibold text-gray-100 whitespace-nowrap">
                  {s.orders}
                  {tracked && s.diverted ? (
                    <span className="text-orange-400/80 text-xs font-normal" title={`Went to Label issue / Hard to find / Can't find mid-round — still counted: ${s.diverted_to || ''}`}> · {s.diverted} diverted</span>
                  ) : null}
                  {tracked && s.excluded ? (
                    <span className="text-gray-600 text-xs font-normal" title="Moved back to New (or elsewhere) — not part of this round"> · {s.excluded} excl.</span>
                  ) : null}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {tracked ? <>
                    <span className="text-gray-200">{s.item_scans ?? 0}</span>
                    {s.manual_packs ? <span className="text-amber-400 text-xs" title="packed with the Pack button, no barcode"> · {s.manual_packs} manual</span> : null}
                    {s.scan_fails ? <span className="text-gray-600 text-xs" title="scans the WMS refused (wrong item / over-scan)"> · {s.scan_fails} refused</span> : null}
                  </> : <span className="text-gray-700">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap text-xs">
                  {tracked && (s.pick_items_per_hour != null || s.pack_items_per_hour != null) ? <>
                    <span className="text-gray-300" title="picking: pieces per hour">{s.pick_items_per_hour != null ? `${s.pick_items_per_hour}` : '—'}</span>
                    <span className="text-gray-600"> · </span>
                    <span className="text-gray-300" title="packing: pieces per hour">{s.pack_items_per_hour != null ? `${s.pack_items_per_hour}` : '—'}</span>
                  </> : <span className="text-gray-700">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right">
                  {s.solving_orders > 0
                    ? <span className="text-orange-400 font-medium">{s.solving_orders}</span>
                    : <span className="text-gray-700">—</span>}
                </td>
                <td className={`px-3 py-2.5 text-right font-bold whitespace-nowrap ${speedColor(live ? live.ifEnded : s.orders_per_hour)}`}>
                  {live ? (
                    <span title={`If the round ended now: ${s.orders} orders over ${fmtDur(live.elapsed)}. Pace of what is actually processed: ${live.done} of ${s.orders}${live.pace != null ? ` = ${live.pace}/hr` : ''}. Ticks every 15 s; reload for fresh counts.`}>
                      {live.ifEnded != null ? `~${live.ifEnded}/hr` : <span className="text-gray-600 font-normal">…</span>}
                      <div className="text-[10px] font-normal text-gray-500">if ended now{live.pace != null ? ` · ${live.pace}/hr done` : ''}</div>
                    </span>
                  ) : s.orders_per_hour !== null ? `${s.orders_per_hour}/hr` : <span className="text-gray-700 font-normal">—</span>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
